import { exponentialBackoff, unwrapOr } from "@zetalabs/core";

export interface Ok<T> {
	success: true;
	value: T;
	error: null;
}

export interface Err<E = Error> {
	success: false;
	value: null;
	error: E;
}

export type Result<T, E = Error> = Ok<T> | Err<E>;
export type AsyncResult<T, E = Error> = Promise<Result<T, E>>;

export type ExtractSuccess<T> = T extends Result<infer U, any> ? U : never;

export function isOk<T, E = Error>(result: Result<T, E>): result is Ok<T> {
	return result.success;
}

export function isErr<T, E = Error>(result: Result<T, E>): result is Err<E> {
	return !result.success;
}

export function ok<T>(value: T): Ok<T> {
	return { success: true, value, error: null };
}

export function err<E = Error>(error: E): Err<E> {
	return { success: false, value: null, error };
}

export async function tryPromise<T>(
	fn: (...args: any[]) => Promise<T>,
	errorMessage?: string,
): Promise<Result<T>> {
	try {
		const value = await fn();
		return ok(value);
	} catch (error) {
		return err(
			unwrapOr(
				error,
				new Error(
					errorMessage || "Unknown error while executing in `tryPromise`",
				),
			),
		);
	}
}

export function tryFn<T>(
	fn: (...args: any[]) => T,
	errorMessage?: string,
): Result<T> {
	try {
		const value = fn();
		return ok(value);
	} catch (error) {
		return err(
			unwrapOr(
				error,
				new Error(errorMessage || "Unknown error while executing in `try`"),
			),
		);
	}
}

export async function withRetries<T = any>(
	fn: (...args: any[]) => Promise<T>,
	configuration?: Partial<{
		retries: number;
		delay: number;
		timeout: number;
		errorMessage: string;
	}>,
): AsyncResult<T> {
	const { retries, delay, timeout, errorMessage } = Object.assign(
		{
			retries: 3,
			delay: 1000,
			timeout: 5000,
			errorMessage: "Unknown error while executing in `withRetries`",
		},
		configuration,
	);

	try {
		const result = await Promise.race([
			fn().then(ok),
			new Promise<Result<T>>((_, reject) =>
				setTimeout(
					() => reject(err(new Error("Operation timed out"))),
					timeout,
				),
			),
		]);

		if (isErr(result)) {
			throw result.error;
		}

		return result;
	} catch (e) {
		if (retries > 0) {
			await new Promise((resolve) => setTimeout(resolve, delay));
			const r = retries - 1;

			return withRetries(fn, {
				retries: r,
				delay: exponentialBackoff(delay, r),
				timeout,
				errorMessage,
			});
		}

		return Promise.reject(err(e));
	}
}
